import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaApple, FaGooglePlay } from "react-icons/fa";
import "./HomePageChrome.css";

const DOWNLOAD_APP_URL = "https://vibereserve.app/lendingPage/";
const PRIVACY_POLICY_URL = "https://vibereserve.app/lendingPage/privacy-policy.html";
const TERMS_CONDITIONS_URL = "https://vibereserve.app/lendingPage/terms_condition.html";
const IQLANCE_SITE_URL = "https://iqlance.ca/";
const APP_STORE_URL = process.env.REACT_APP_APP_STORE_URL || "https://apps.apple.com/";
const PLAY_STORE_URL = process.env.REACT_APP_PLAY_STORE_URL || "https://play.google.com/store";

const HEADER_SCROLL_THRESHOLD_PX = 16;

const BRAND_LOGO_SRC =
  (typeof process.env.REACT_APP_BRAND_LOGO_URL === "string" && process.env.REACT_APP_BRAND_LOGO_URL.trim()) ||
  `${process.env.PUBLIC_URL || ""}/assets/images/logowhite.svg`;

const IQLANCE_LOGO_SRC = `${process.env.PUBLIC_URL || ""}/assets/images/iqlance-logo.svg`;

/**
 * Official VR / Vibe Reserve mark (`public/assets/images/logo.png`).
 * Header uses `compact`; footer uses default height.
 */
function BrandLogo({ className = "", compact = false }) {
  const rootClass = ["hp-chrome-brand", "hp-chrome-brand--asset", compact ? "hp-chrome-brand--compact" : "", className]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootClass.trim()}>
      <img src={BRAND_LOGO_SRC} alt="Vibe Reserve" className="hp-chrome-brand__logo-img" draggable={false} />
    </div>
  );
}

export function HomePageMarketingHeader({ onBecomeHost, onJoinPromoter, onBecomePromoter }) {
  const [scrolled, setScrolled] = useState(() =>
    typeof window !== "undefined" ? window.scrollY > HEADER_SCROLL_THRESHOLD_PX : false
  );

  const onScroll = useCallback(() => {
    const next = window.scrollY > HEADER_SCROLL_THRESHOLD_PX;
    setScrolled((prev) => (prev === next ? prev : next));
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  return (
    <header className={`hp-chrome-header${scrolled ? " hp-chrome-header--scrolled" : ""}`}>
      <div className="hp-chrome-header__bar">
        <div className="hp-chrome-header__inner">
          <Link to="/" className="hp-chrome-header__logo-link" aria-label="Vibe Reserve home">
            <BrandLogo compact />
          </Link>
          <nav className="hp-chrome-header__nav" aria-label="Primary actions">
            <div className="hp-chrome-tabs" role="group" aria-label="Promoter options">
              <button type="button" className="hp-chrome-tab" onClick={onBecomeHost}>
                Become Host
              </button>
              <button type="button" className="hp-chrome-tab" onClick={onBecomePromoter}>
                Become a Promoter
              </button>
              <button type="button" className="hp-chrome-tab" onClick={onJoinPromoter}>
                Join a Promoter
              </button>
            </div>
            <a
              className="hp-chrome-cta"
              href={DOWNLOAD_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Download Application
            </a>
          </nav>
        </div>
      </div>
    </header>
  );
}

function scrollToTop() {
  window.scrollTo({ top: 0, behavior: "smooth" });
}

export function HomePageMarketingFooter() {
  const year = new Date().getFullYear();

  return (
    <footer className="hp-chrome-footer">
      <div className="hp-chrome-footer__top">
        <div className="hp-chrome-footer__inner">
          <div className="hp-chrome-footer__brand-block">
            <BrandLogo className="hp-chrome-footer__brand" />
          </div>
          <div className="hp-chrome-footer__stores">
            <a href={PLAY_STORE_URL} className="hp-chrome-store-badge" target="_blank" rel="noopener noreferrer">
              <FaGooglePlay className="hp-chrome-store-badge__icon" aria-hidden />
              <span className="hp-chrome-store-badge__lines">
                <span className="hp-chrome-store-badge__small">Available on</span>
                <span className="hp-chrome-store-badge__main">Google Play</span>
              </span>
            </a>
            <a href={APP_STORE_URL} className="hp-chrome-store-badge" target="_blank" rel="noopener noreferrer">
              <FaApple className="hp-chrome-store-badge__icon" aria-hidden />
              <span className="hp-chrome-store-badge__lines">
                <span className="hp-chrome-store-badge__small">Download on</span>
                <span className="hp-chrome-store-badge__main">App Store</span>
              </span>
            </a>
          </div>
        </div>
      </div>

      <div className="hp-chrome-footer__divider" />

      <div className="hp-chrome-footer__bottom">
        <div className="hp-chrome-footer__inner hp-chrome-footer__inner--bottom">
          <p className="hp-chrome-footer__legal">
            Copyright © {year}{" "}
            <Link to="/" className="hp-chrome-footer__accent-link">
              Vibe Reserve
            </Link>{" "}
            All Rights Reserved |{" "}
            <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" className="hp-chrome-footer__accent-link">
              Privacy Policy
            </a>{" "}
            |{" "}
            <a href={TERMS_CONDITIONS_URL} target="_blank" rel="noopener noreferrer" className="hp-chrome-footer__accent-link">
              Terms and conditions
            </a>
            .
          </p>
          <div className="hp-chrome-footer__right-cluster">
            <div className="hp-chrome-footer__credits">
              <a href={IQLANCE_SITE_URL} target="_blank" rel="noopener noreferrer" className="hp-chrome-footer__iq-logo-link">
                <img src={IQLANCE_LOGO_SRC} alt="iQlance" className="hp-chrome-footer__iq-logo-img" draggable={false} />
              </a>
              <p className="hp-chrome-footer__credits-line">
                DESIGNED AND DEVELOPED BY:{" "}
                <a href={IQLANCE_SITE_URL} target="_blank" rel="noopener noreferrer" className="hp-chrome-footer__accent-link">
                  iQlance Solutions Ltd.
                </a>
              </p>
            </div>
            <button type="button" className="hp-chrome-footer__top-btn" onClick={scrollToTop} aria-label="Scroll to top">
              <span aria-hidden>^</span>
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
